import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { AppService } from '@app/app.service';
import { CUSTOM_YEAR_MONTH_FORMATS, CustomDatePickerAdapter } from '@app/core/date-adapter';
import { getLanguage } from '@app/core/material.module';
import { FuiDestroyService } from '@services/destroy.service';
import * as moment from 'moment';
import { Moment } from 'moment';
import { takeUntil } from 'rxjs/operators';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';

@Component({
    selector: 'app-date-month',
    templateUrl: './date-month.component.html',
    styleUrls: ['../input/input.scss', '../date/date.component.scss', './date-month.component.css'],
    providers: [
        FuiDestroyService,
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_YEAR_MONTH_FORMATS },
        { provide: MAT_DATE_LOCALE, deps: [AppService], useFactory: getLanguage },
        { provide: DateAdapter, useClass: CustomDatePickerAdapter, deps: [MAT_DATE_LOCALE] },
    ],
})
export class DateMonthComponent implements OnInit {
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);
    readonly appService = inject(AppService);

    field: FieldConfig;
    group: FormGroup;
    data: any;
    max: Moment | null = null;
    min: Moment | null = null;

    /** Событие TrailingButton */
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    dataFilter = (d: Moment | null): boolean => {
        if (d === null || !moment.isMoment(d)) {
            return true;
        }
        if (!this.max && !this.min) {
            return true;
        }
        if (!this.max && this.min && moment.isMoment(this.min)) {
            return d.isSameOrAfter(this.min);
        }
        if (this.max && !this.min && moment.isMoment(this.max)) {
            return d.isSameOrBefore(this.max);
        }
        if (this.max && this.min) {
            return d.isBetween(this.max, this.min);
        }
        return true;
    };

    ngOnInit() {
        this.getMax();
        this.getMin();
        if (this.data) {
            if (this.data.maxField && this.group.get(this.data.maxField)) {
                this.group
                    .get(this.data.maxField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMax();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.maxField).markAsTouched();
                        this.group.get(this.data.maxField).updateValueAndValidity({ emitEvent: false });
                    });
            }
            if (this.data.minField && this.group.get(this.data.minField)) {
                this.group
                    .get(this.data.minField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMin();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.minField).markAsTouched();
                        this.group.get(this.data.minField).updateValueAndValidity({ emitEvent: false });
                    });
            }
        }

        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
    }

    /**
     * Изменение года
     * @param normalizedYear выбранный год
     */
    chosenYearHandler(normalizedYear: Moment) {
        let ctrlValue = normalizedYear;
        if (typeof ctrlValue == 'string') {
            ctrlValue = moment(ctrlValue, 'MM.YYYY')
                .date(1)
                .hour(0)
                .minute(0)
                .second(0)
                .add(moment().utcOffset(), 'm')
                .utc(false);
        }
        ctrlValue.year(normalizedYear.year());
        this.group.get(this.field.name).setValue(ctrlValue);
    }

    /**
     * Изменение месяца
     * @param normalizedMonth месяц
     * @param datepicker поле
     */
    chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<any>) {
        let ctrlValue = normalizedMonth;
        if (typeof ctrlValue == 'string') {
            ctrlValue = moment(ctrlValue, 'MM.YYYY')
                .date(1)
                .hour(0)
                .minute(0)
                .second(0)
                .add(moment().utcOffset(), 'm')
                .utc(false);
        }
        ctrlValue.month(normalizedMonth.month());
        this.group.get(this.field.name).setValue(ctrlValue);
        datepicker.close();
    }

    /**
     * Получение максимального ограничения
     */
    getMax() {
        if (this.data && this.data.maxDate) {
            this.max = this.data.maxDate;
        } else if (this.data && this.data.maxField) {
            const value = this.group.get(this.data.maxField).value;
            if (!value) {
                this.max = null;
                return;
            }
            switch (typeof value) {
                case 'string':
                    this.max = moment(this.group.get(this.data.maxField).value, 'MM.YYYY');
                    if (!this.max.isValid()) {
                        this.max = moment(this.group.get(this.data.maxField).value, 'YYYY-MM');
                    }
                    break;
                case 'object':
                    this.max = this.group.get(this.data.maxField).value;
                    break;
            }
            if (!this.max.isValid()) {
                this.max = null;
            }
        } else {
            this.max = null;
        }
    }

    /** Получение минимального ограничения */
    getMin() {
        if (this.data && this.data.minDate) {
            this.min = this.data.minDate;
        } else if (this.data && this.data.minField) {
            const value = this.group.get(this.data.minField).value;
            if (!value) {
                this.min = null;
                return;
            }
            switch (typeof value) {
                case 'string':
                    this.min = moment(this.group.get(this.data.minField).value, 'MM.YYYY');
                    if (!this.min.isValid()) {
                        this.min = moment(this.group.get(this.data.minField).value, 'YYYY-MM');
                    }
                    break;
                case 'object':
                    this.min = this.group.get(this.data.minField).value;
                    break;
            }
            if (!this.min.isValid()) {
                this.min = null;
            }
        } else {
            this.min = null;
        }
    }

    /**
     * При открытии дата-пикера пробуем пересчитать интервал
     * @param datepicker
     */
    public inputDatepickerOpened() {
        this.getMax();
        this.getMin();
    }

    /**
     * Изменить доступность редактирования значения компоненты
     * @param disabled Компонента доступна или недоступна для редактирования?
     */
    setDisabled(disabled: boolean) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }
}
